'use client';

import { m, LazyMotion, domAnimation, useInView } from 'framer-motion';
import { useRef } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { useLocale } from 'src/locales';

// ----------------------------------------------------------------------

export default function OfficialCompanyDetails() {
  const { t } = useLocale();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const typographyProps = {
    variant: 'subtitle2',
    sx: { mb: 1, fontSize: 16, fontWeight: 700 },
  };

  // Motion variant for each row
  const rowVariant = (delay = 0) => ({
    initial: { opacity: 0, y: 20 },
    animate: isInView ? { opacity: 1, y: 0 } : {},
    transition: { duration: 0.6, delay },
  });

  return (
    <LazyMotion features={domAnimation}>
      <Box
        ref={ref}
        component="section"
        sx={{
          pt: { xs: 5, md: 5 },
          pb: { xs: 5, md: 5 },
          textAlign: { xs: 'left', md: 'center' },
          overflow: 'hidden',
        }}
      >
        <Container>
          {/* Heading */}
          <m.div {...rowVariant(0)}>
            <Typography variant="h3" sx={{ mb: 6, fontSize: '2rem !important' }}>
              {t('Official_Company_Details')}
            </Typography>
          </m.div>

          {/* Center wrapper to align columns together */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: { xs: 'left', md: 'center' },
                alignItems: 'flex-start',
                gap: 6,
                flexWrap: 'wrap',
                width: 'fit-content',
                mx: 'auto',
                position: 'relative',
                left: { xs: 0, md: '8%' },
              }}
            >
              {/* First Column */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  textAlign: { xs: 'left', md: 'right' },
                  gap: 2,
                }}
              >
                {[
                  {
                    label: 'Official_Company_Name',
                    value: 'Feker for Communications & Information Technology Co.',
                  },
                  { label: 'Commercial_Registration_Number', value: '1010582007' },
                  { label: 'CR_Issue_Date', value: 'June 11, 2019' },
                  { label: 'Unified_National_Number', value: '7015321933' },
                ].map((item, i) => (
                  <m.div key={item.label} {...rowVariant(0.2 * i)}>
                    <Typography {...typographyProps}>{t(item.label)}</Typography>
                    <Typography variant="body2">{item.value}</Typography>
                  </m.div>
                ))}
              </Box>

              {/* Divider */}
              <Box
                component={'div'}
                display={{ xs: 'none', md: 'flex' }}
                style={{
                  height: '100%',
                  border: '1px dashed #C4CDD5',
                }}
              />

              {/* Second Column */}
              <Box sx={{ display: 'flex', flexDirection: 'column', textAlign: 'left', gap: 2 }}>
                {[
                  { label: 'Entity_Type', value: 'Limited Liability Company (LLC)' },
                  { label: 'Transport_General_Authority_License', value: 'No. 00000747/29' },
                  {
                    label: 'Trade_Mark',
                    value:
                      'Registered with the Saudi Authority for Intellectual Property under No. 1442022156',
                  },
                ].map((item, i) => (
                  <m.div key={item.label} {...rowVariant(0.3 + 0.2 * i)}>
                    <Typography {...typographyProps}>{t(item.label)}</Typography>
                    <Typography variant="body2">{item.value}</Typography>
                  </m.div>
                ))}
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
    </LazyMotion>
  );
}
